/**
 * String Utilities
 *
 * Common string operations and custom helpers built on top of lodash
 *
 * @author Housefolios
 * @created 2025-01-27
 */

import {
  toLower,
  toUpper,
  trim,
  replace,
  split,
  join,
  padStart,
  truncate,
  startCase,
  kebabCase,
  capitalize,
  upperFirst,
  startsWith,
  toString,
  isString,
  isEmpty,
  isNil,
} from 'lodash'

/**
 * Safely convert value to string
 * @param {*} value - The value to convert
 * @param {string} defaultValue - Default value if conversion fails
 * @returns {string} String representation of value
 */
export const safeToString = (value, defaultValue = '') => {
  if (isNil(value)) return defaultValue
  return toString(value)
}

/**
 * Safely convert string to lowercase
 * @param {string} value - The string to convert
 * @param {string} defaultValue - Default value if conversion fails
 * @returns {string} Lowercase string
 */
export const safeToLower = (value, defaultValue = '') => {
  if (!isString(value)) return defaultValue
  return toLower(value)
}

/**
 * Safely convert string to uppercase
 * @param {string} value - The string to convert
 * @param {string} defaultValue - Default value if conversion fails
 * @returns {string} Uppercase string
 */
export const safeToUpper = (value, defaultValue = '') => {
  if (!isString(value)) return defaultValue
  return toUpper(value)
}

/**
 * Safely trim whitespace from string
 * @param {string} value - The string to trim
 * @param {string} defaultValue - Default value if conversion fails
 * @returns {string} Trimmed string
 */
export const safeTrim = (value, defaultValue = '') => {
  if (!isString(value)) return defaultValue
  return trim(value)
}

/**
 * Check if string is empty or whitespace only
 * @param {string} value - The string to check
 * @returns {boolean} True if string is empty or whitespace only
 */
export const isBlank = (value) => {
  if (!isString(value)) return true
  return trim(value) === ''
}

/**
 * Check if string is not empty and not whitespace only
 * @param {string} value - The string to check
 * @returns {boolean} True if string has content
 */
export const isNotBlank = (value) => {
  return !isBlank(value)
}

/**
 * Capitalize first letter of each word
 * @param {string} value - The string to capitalize
 * @returns {string} String with capitalized words
 */
export const titleCase = (value) => {
  if (!isString(value)) return ''
  return startCase(toLower(value))
}

/**
 * Convert string to camelCase
 * @param {string} value - The string to convert
 * @returns {string} camelCase string
 */
export const toCamelCase = (value) => {
  if (!isString(value)) return ''
  return value
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]+(.)/g, (match, chr) => chr.toUpperCase());
}

/**
 * Convert string to PascalCase
 * @param {string} value - The string to convert
 * @returns {string} PascalCase string
 */
export const toPascalCase = (value) => {
  if (!isString(value)) return ''
  const camelCase = toCamelCase(value)
  return upperFirst(camelCase)
}

/**
 * Convert string to snake_case
 * @param {string} value - The string to convert
 * @returns {string} snake_case string
 */
export const toSnakeCase = (value) => {
  if (!isString(value)) return ''
  return value
    .replace(/([A-Z])/g, '_$1')
    .toLowerCase()
    .replace(/^_/, '');
}

/**
 * Truncate string with ellipsis
 * @param {string} value - The string to truncate
 * @param {number} length - Maximum length
 * @param {string} suffix - Suffix to add (default: '...' })
 * @returns {string} Truncated string
 */
export const truncateWithEllipsis = (
  value,
  length,
  suffix = '...',
) => {
  if (!isString(value)) return ''
  return truncate(value, { length, omission: suffix  })
}

/**
 * Remove special characters from string
 * @param {string} value - The string to clean
 * @param {boolean} keepSpaces - Whether to keep spaces
 * @returns {string} Cleaned string
 */
export const removeSpecialChars = (value, keepSpaces = true) => {
  if (!isString(value)) return ''
  const pattern = keepSpaces ? /[^a-zA-Z0-9\s]/g : /[^a-zA-Z0-9]/g
  return value.replace(pattern, '')
}

/**
 * Extract numbers from string
 * @param {string} value - The string to extract from
 * @returns {string} String containing only numbers
 */
export const extractNumbers = (value) => {
  if (!isString(value)) return ''
  return value.replace(/\D/g, '');
}

/**
 * Extract letters from string
 * @param {string} value - The string to extract from
 * @param {boolean} keepSpaces - Whether to keep spaces
 * @returns {string} String containing only letters
 */
export const extractLetters = (value, keepSpaces = true) => {
  if (!isString(value)) return ''
  const pattern = keepSpaces ? /[^a-zA-Z\s]/g : /[^a-zA-Z]/g
  return value.replace(pattern, '')
}

/**
 * Check if string contains only numbers
 * @param {string} value - The string to check
 * @returns {boolean} True if string contains only numbers
 */
export const isNumeric = (value) => {
  if (!isString(value)) return false
  return /^\d+$/.test(value);
}

/**
 * Check if string is a valid email
 * @param {string} value - The string to check
 * @returns {boolean} True if string is a valid email
 */
export const isValidEmail = (value) => {
  if (!isString(value)) return false
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(value)
}

/**
 * Check if string is a valid phone number (basic US format)
 * @param {string} value - The string to check
 * @returns {boolean} True if string is a valid phone number
 */
export const isValidPhoneNumber = (value) => {
  if (!isString(value)) return false
  const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/
  const cleanNumber = extractNumbers(value)
  return phoneRegex.test(cleanNumber)
}

/**
 * Format string as phone number
 * @param {string} value - The string to format
 * @returns {string} Formatted phone number
 */
export const formatPhoneNumber = (value) => {
  if (!isString(value)) return ''
  const numbers = extractNumbers(value)
  if (numbers.length === 10) {
    return `(${numbers.slice(0, 3) }) ${numbers.slice(3, 6)}-${numbers.slice(6)}`
  }
  if (numbers.length === 11 && numbers[0] === '1') {
    return `+1 (${numbers.slice(1, 4) }) ${numbers.slice(4, 7)}-${numbers.slice(7)}`
  }
  return value
}

/**
 * Generate initials from name
 * @param {string} name - The name to generate initials from
 * @param {number} maxInitials - Maximum number of initials
 * @returns {string} Initials
 */
export const getInitials = (name, maxInitials = 2) => {
  if (!isString(name)) return ''
  const words = split(trim(name), ' ')
  const initials = words
    .slice(0, maxInitials)
    .map((word) => upperFirst(word.charAt(0)))
    .join('')
  return initials
}

/**
 * Mask sensitive information in string
 * @param {string} value - The string to mask
 * @param {string} maskChar - Character to use for masking
 * @param {number} visibleChars - Number of characters to keep visible
 * @returns {string} Masked string
 */
export const maskString = (
  value,
  maskChar = '*',
  visibleChars = 4,
) => {
  if (!isString(value) || value.length <= visibleChars) return value
  const visible = value.slice(-visibleChars)
  const masked = maskChar.repeat(value.length - visibleChars)
  return masked + visible
}

// Re-export commonly used lodash string functions
export {
  toLower,
  toUpper,
  trim,
  replace,
  split,
  join,
  padStart,
  truncate,
  startCase,
  kebabCase,
  capitalize,
  upperFirst,
  startsWith,
  toString,
  isString,
  isEmpty,
  isNil,
}
